import { useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { ISportParticipantDetail } from "interfaces";
import Button from "components/button";
import { Link } from "react-router-dom";
import { DownloadIcon, PencilIcon } from "@heroicons/react/outline";
import { HistoryLogButton } from "components/historyLogButton";
import { EwaiverInformationSection, InformationSection, RegistrationInformationSection } from "./components";
import { isHaveActionPermission } from "utills/helpers";
import toast from "react-hot-toast";

export const SportParticipantDetail: React.FC = () => {
  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<ISportParticipantDetail>({
    resource: "participant",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });
  
  const participantDetailData = data?.data ?? null;
  const eWaiverFileUrl = data?.data?.participant?.e_waiver_file;

  // console.log({ eWaiverFileUrl });

  const handleDownloadFile = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const handleGetEwaiverFileName = (detailParticipant: ISportParticipantDetail) => {
    if (!detailParticipant) return "unknown_participant";

    return `eWaiver_${detailParticipant?.participant?.bib}`;
  }

  const handleExportEwaiver = (participant: ISportParticipantDetail | undefined, url: string) => {
    if (!participant) {
      toast.error("Export failed, invalid paticipant");
      return;
    }

    if (url === "") {
      toast.error("Export failed, invalid path");
      return;
    }

    handleDownloadFile(url, handleGetEwaiverFileName(participant));
  };

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Sport participant detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            {eWaiverFileUrl && eWaiverFileUrl !== "" && (
              <Button
                type="button"
                className="w-full bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px]
                            hover:bg-white active:bg-white active:opacity-85"
                onClick={() =>
                  handleExportEwaiver(data?.data, eWaiverFileUrl ?? "")
                }
              >
                <DownloadIcon className="w-3 h-4 text-brand-500 mr-2" /> Export
                eWaiver
              </Button>
            )}
            <div>
              <HistoryLogButton
                className="w-11 h-11"
                resource={`participant/${participantDetailData?.participant.participant_id}/history`}
              />
            </div>
            {isHaveActionPermission("sports", "participants", "edit") ? (
              <Link
                to={`/participants/sport/${participantDetailData?.participant.participant_id}/edit`}
              >
                <Button className="w-11 h-11 sm:w-auto">
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : <></>}
          </div>
        }
      />
      {participantDetailData ? (
        <>
          <InformationSection participantDetailData={participantDetailData} />
          <RegistrationInformationSection participantDetailData={participantDetailData} />
          <EwaiverInformationSection participantDetailData={participantDetailData} />
        </>
      ) : <></>}

      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}